import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Grid, Typography, Card, Container, alpha, } from '@mui/material';
import { Assignment as AssignmentIcon, PendingActions as PendingActionsIcon, EventBusy as EventBusyIcon, EventAvailable as EventAvailableIcon, CalendarMonth as CalendarIcon, AccessTime as AccessTimeIcon } from "@mui/icons-material";
import { getData } from "../../services/axiosInstance";
import { API_ENDPOINTS } from "../../services/endpoints";
import PushNotificationManager from "../../services/PushNotificationManager";
import Loader from "../../common/spinner";

function EmployeeDashboard() {
    const navigate = useNavigate();
    const [stats, setStats] = useState({});
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userId = userData?.id;
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            if (!userId) return;
            setLoading(true);

            try {
                const url = `${API_ENDPOINTS.getEmployeeDashboardCounts}?userId=${userId}`;
                const res = await getData(url);

                if (res.status === "SUCCESS") {
                    setStats(res.message?.data || {});
                }
            } catch (e) {
                return null;
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, [userId]);

    const cards = [
        {
            id: 1,
            icon: <AssignmentIcon />,
            title: "Total Projects",
            count: stats.totalProjects || 0,
            p: "#243B55", s: "#141E30",
            path: "/employee/my-project-list"
        },
        {
            id: 2,
            icon: <PendingActionsIcon />,
            title: "Active Tasks",
            count: stats.totalIncompleteTasks || 0,
            p: "#6a11cb", s: "#2575fc",
            path: "/employee/my-tasks"
        },
        {
            id: 3,
            icon: <EventAvailableIcon />,
            title: "Monthly Attendance",
            count: stats.monthlyAttendancePercentage
                ? `${parseFloat(stats.monthlyAttendancePercentage)}%`
                : "0%",
            p: "#11998e", s: "#38ef7d",
            path: "/employee/my-attendance"
        },
        {
            id: 4,
            icon: <EventBusyIcon />,
            title: "Leave Balance",
            count: stats.totalLeaveBalance || 0,
            p: "#f093fb", s: "#f5576c",
            path: "/employee/manage-leave-requests"
        },
        {
            id: 5,
            icon: <CalendarIcon />,
            title: "Upcoming Holiday",
            // We store the name in 'count' and the date in a new property 'date'
            count: stats.upcomingHolidays?.[0]?.holiday_name || "None",
            date: stats.upcomingHolidays?.[0]?.date
                ? new Date(stats.upcomingHolidays[0].date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                : "",
            p: "#FF8C00", s: "#FFA500",
            path: "/employee/manage-leave-requests"
        },
        {
            id: 6,
            icon: <AccessTimeIcon />,
            title: "This Week's Timesheet",
            count: ({
                NOT_STARTED: 'Not Started', DRAFT: 'Draft', SUBMITTED: 'Submitted',
                L1_APPROVED: 'L1 Approved', PENDING_LEVEL_2: 'Pending L2',
                APPROVED: 'Approved', REJECTED: 'Rejected'
            })[stats.thisWeekTimesheetStatus] || 'Not Started',
            p: "#5B21B6", s: "#8B5CF6",
            path: "/employee/my-timesheets",
            isText: true
        }
    ];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                <Loader />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 1 }}>
            <PushNotificationManager userId={userId} />
            <Container maxWidth="xl" sx={{ padding: '20px 0 !important' }}>
                <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 1, sm: 8, md: 12 }}>
                    {cards.map(c => (
                        <Grid key={c.id} size={{ xs: 4, sm: 4, md: 6, lg: 4 }}>
                            <Card
                                onClick={() => c.path && navigate(c.path)}
                                sx={{
                                    borderRadius: '24px',
                                    cursor: 'pointer',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    flex: 1,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    transition: '0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    px: 3,
                                    py: 3,
                                    background: '#fff',
                                    '&:before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: 0,
                                        background: `linear-gradient(135deg, ${alpha(c.p, 0.08)}, ${alpha(c.s, 0.08)})`,
                                        clipPath: 'circle(70% at 100% 0%)',
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-10px)',
                                        boxShadow: `0 20px 40px ${alpha(c.p, 0.15)}`,
                                    }
                                }}
                            >
                                <Box sx={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <Box>
                                        <Box sx={{
                                            width: 48, height: 48, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: `linear-gradient(135deg, ${c.p}, ${c.s})`, mb: 1.5
                                        }}>
                                            {React.cloneElement(c.icon, { sx: { fontSize: 24, color: '#fff' } })}
                                        </Box>
                                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>
                                            {c.title}
                                        </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography
                                            variant="h4"
                                            sx={{
                                                fontWeight: 800,
                                                color: c.p,
                                                fontSize: (c.id === 5 || c.isText) ? '1.2rem' : '2rem',
                                                lineHeight: 1.2
                                            }}
                                        >
                                            {c.count ?? 0}
                                        </Typography>

                                        {/* Only show the date for the Holiday Card */}
                                        {c.date && (
                                            <Typography
                                                variant="subtitle2"
                                                sx={{
                                                    color: '#64748b',
                                                    fontWeight: 700,
                                                    mt: 0.5
                                                }}
                                            >
                                                {c.date}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Container >
        </Box>
    );
}

export default EmployeeDashboard;